import { useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';
export type SortValue = string | number | boolean | null | undefined;

/// Sortérbare kolonneoverskrifter. `get` henter værdien der sorteres på, så tal
/// sorteres som tal og tekst med dansk collation.
export function useSort<T>(
  rows: T[],
  get: (row: T, key: string) => SortValue,
  initialKey: string,
  initialDir: SortDir = 'asc',
) {
  const [key, setKey] = useState(initialKey);
  const [dir, setDir] = useState<SortDir>(initialDir);

  const toggle = (nextKey: string) => {
    if (nextKey === key) {
      setDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setKey(nextKey);
      setDir('asc');
    }
  };

  const sorted = useMemo(() => {
    const out = [...rows].sort((a, b) => {
      const av = get(a, key);
      const bv = get(b, key);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      if (typeof av === 'boolean' && typeof bv === 'boolean') return Number(bv) - Number(av);
      return String(av).localeCompare(String(bv), 'da');
    });
    return dir === 'asc' ? out : out.reverse();
    // `get` er stabil pr. render i alle kaldesteder og holdes ude af listen med vilje.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, key, dir]);

  return { key, dir, toggle, sorted };
}
